import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CalendarClock, ChevronRight, CircleAlert, FileText, Megaphone, MessageSquareReply, Plus, RefreshCw, Search, Send, UsersRound,
} from 'lucide-react';
import { WsSegmented } from 'src/stage/WsControls';
import { CharacterStage, Kpi, PageHeader, Skeleton, StagePage, Status, formatNumber, formatWhen } from 'src/stage/StageUi';
import { characterFor } from 'src/stage/characters';
import broadcastApi, { currentConnection, readErrors } from './broadcastApi';
import { BROADCAST_STATUS, audienceLabel, isOverdue, peopleCount, progress, rate } from './broadcastModel';
import './broadcasts.css';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'sending', label: 'Sending' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'completed', label: 'Sent' },
  { id: 'cancelled', label: 'Stopped' },
];

function useBroadcasts(phoneNumberId, status, search) {
  const [state, setState] = useState({ loading: true, rows: [], counts: {}, totals: null, error: null });
  const sequence = useRef(0);
  const load = useCallback(async (quiet = false) => {
    const version = ++sequence.current;
    if (!quiet) setState((current) => ({ ...current, loading: true, error: null }));
    try {
      const data = await broadcastApi.list(phoneNumberId, { status, search: search || undefined, per_page: 50 });
      if (version !== sequence.current) return;
      setState({ loading: false, rows: data.data || [], counts: data.counts || {}, totals: data.totals || null, error: null });
    } catch (error) {
      if (version !== sequence.current) return;
      setState((current) => ({ ...current, loading: false, error: readErrors(error) }));
    }
  }, [phoneNumberId, status, search]);
  useEffect(() => { load(); }, [load]);
  // Keep sending broadcasts moving on screen.
  const live = state.rows.some((row) => row.status === 'sending' || (row.status === 'scheduled' && new Date(row.scheduled_at) <= new Date()));
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => load(true), 4000);
    return () => clearInterval(timer);
  }, [live, load]);
  return [state, load];
}

function Funnel({ stats }) {
  const sent = stats.sent || 0;
  return (
    <div className="wa-bc-mini" aria-label="Results">
      <span><strong className="ws-num">{formatNumber(sent)}</strong><small>Sent</small></span>
      <span><strong className="ws-num">{rate(stats.delivered, sent) ?? '—'}{sent ? '%' : ''}</strong><small>Delivered</small></span>
      <span><strong className="ws-num">{rate(stats.read, sent) ?? '—'}{sent ? '%' : ''}</strong><small>Read</small></span>
      <span><strong className="ws-num">{formatNumber(stats.replied || 0)}</strong><small>Replied</small></span>
    </div>
  );
}

function BroadcastRow({ broadcast }) {
  const meta = BROADCAST_STATUS[broadcast.status] || BROADCAST_STATUS.completed;
  const stats = broadcast.stats || {};
  const done = progress(stats);
  const when = broadcast.status === 'scheduled' ? (isOverdue(broadcast) ? `Due ${formatWhen(broadcast.scheduled_at)} · starting now` : `Sends ${formatWhen(broadcast.scheduled_at)}`)
    : broadcast.status === 'cancelled' ? `Stopped ${formatWhen(broadcast.cancelled_at)}`
      : `Started ${formatWhen(broadcast.started_at || broadcast.scheduled_at)}`;
  return (
    <li className="wa-list-item">
      <Link to={`/broadcast/${broadcast.id}`} className="wa-list-link wa-bc-row" aria-label={`${broadcast.name}, ${meta.label}`}>
        <span className="wa-bc-name">
          <span className={`wa-bc-icon wa-bc-icon--${broadcast.status}`} aria-hidden="true">
            {broadcast.status === 'scheduled' ? <CalendarClock size={18} /> : <Megaphone size={18} />}
          </span>
          <span style={{ minWidth: 0 }}>
            <strong className="wa-ellipsis">{broadcast.name}</strong>
            <span className="ws-muted wa-ellipsis"><FileText size={12} aria-hidden="true" /> {broadcast.template?.name} · {audienceLabel(broadcast.audience)}</span>
          </span>
        </span>
        <span className="wa-bc-state">
          <Status tone={meta.tone}>{meta.label}</Status>
          {broadcast.status === 'sending'
            ? <span className="ws-meter wa-bc-meter" role="meter" aria-label="Sent so far" aria-valuemin={0} aria-valuemax={100} aria-valuenow={done}><span style={{ width: `${done}%` }} /></span>
            : <span className="ws-muted">{peopleCount(broadcast.total_recipients)}</span>}
        </span>
        {(stats.sent || stats.failed || broadcast.status === 'sending' || broadcast.status === 'completed')
          ? <Funnel stats={stats} />
          : <span className="ws-muted wa-bc-nothing">{broadcast.status === 'scheduled' ? 'Nothing sent yet' : 'Stopped before sending'}</span>}
        <span className="ws-muted wa-bc-when">{when}</span>
        <ChevronRight size={16} className="wa-bc-go" aria-hidden="true" />
      </Link>
    </li>
  );
}

function EmptyBroadcasts({ onCreate }) {
  const steps = [
    { icon: FileText, title: 'Pick an approved template', text: 'Meta approves templates, so broadcasts reach people outside the 24-hour window.' },
    { icon: UsersRound, title: 'Choose who gets it', text: 'Everyone, people with certain tags, or contacts you pick. Blocked contacts are always left out.' },
    { icon: Send, title: 'Send now or schedule', text: 'Watch it deliver live. Replies arrive in Chats, where your team or bot can answer.' },
  ];
  return (
    <section className="ws-card wa-bc-empty ws-rise">
      <CharacterStage character={characterFor('tara')} height={230} />
      <div className="wa-bc-empty-copy">
        <span className="ws-eyebrow">Broadcasts</span>
        <h2 className="ws-h2" style={{ fontSize: 22, marginTop: 6 }}>Send your first broadcast</h2>
        <p className="ws-muted" style={{ fontSize: 14, marginTop: 6 }}>Share an offer, a reminder or news with many customers at once, personalised with each person’s name.</p>
        <ol className="wa-steps">
          {steps.map(({ icon: Icon, title, text }, index) => (
            <li key={title}><span className="wa-step-num">{index + 1}</span><span><strong><Icon size={14} aria-hidden="true" />{title}</strong><span className="ws-muted">{text}</span></span></li>
          ))}
        </ol>
        <button type="button" className="ws-btn ws-btn--primary" onClick={onCreate}><Plus size={16} />New broadcast</button>
      </div>
    </section>
  );
}

export default function BroadcastsPage() {
  const navigate = useNavigate();
  const connection = currentConnection();
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);
  const [state, reload] = useBroadcasts(connection?.phoneNumberId, status, search);
  const totals = state.totals;
  const items = useMemo(() => FILTERS.map((item) => ({ ...item, count: state.counts?.[item.id] ?? undefined })), [state.counts]);
  const create = () => navigate('/broadcast/new');
  const firstRun = !state.loading && !state.error && (state.counts?.all || 0) === 0;

  return (
    <StagePage className="wa-bc" label="Broadcasts">
      <PageHeader
        eyebrow="Reach"
        title="Broadcasts"
        description="Send an approved template to many customers at once. Replies land in Chats."
        actions={!state.error?.forbidden && (
          <>
            <button type="button" className="ws-btn ws-btn--quiet" onClick={() => reload()} aria-label="Refresh broadcasts"><RefreshCw size={15} />Refresh</button>
            <button type="button" className="ws-btn ws-btn--primary" onClick={create}><Plus size={16} />New broadcast</button>
          </>
        )}
      />

      {state.error && (
        <div className={`wa-notice ${state.error.forbidden ? 'wa-notice--info' : 'wa-notice--bad'}`} role="alert">
          <CircleAlert size={16} />
          <span className="ws-grow">{state.error.summary}</span>
          {!state.error.forbidden && <button type="button" className="ws-btn ws-btn--sm ws-btn--quiet" onClick={() => reload()}>Try again</button>}
        </div>
      )}

      {firstRun && <EmptyBroadcasts onCreate={create} />}

      {!firstRun && !state.error?.forbidden && (
        <>
          <section className="ws-card ws-kpis ws-rise" aria-label="Last 30 days">
            <Kpi icon={Send} label="Messages sent · 30 days" loading={state.loading && !totals} value={formatNumber(totals?.sent)} hint={totals ? `${formatNumber(totals.broadcasts)} broadcasts` : null} />
            <Kpi label="Delivered" loading={state.loading && !totals} value={<>{rate(totals?.delivered, totals?.sent) ?? '—'}<small>{totals?.sent ? '%' : ''}</small></>} hint={totals ? peopleCount(totals.delivered) : null} />
            <Kpi label="Read" loading={state.loading && !totals} value={<>{rate(totals?.read, totals?.sent) ?? '—'}<small>{totals?.sent ? '%' : ''}</small></>} hint={totals ? peopleCount(totals.read) : null} />
            <Kpi icon={MessageSquareReply} label="Replied" loading={state.loading && !totals} value={formatNumber(totals?.replied)} hint={totals?.sent ? `${rate(totals.replied, totals.sent)}% of people reached` : null} />
          </section>

          <section className="ws-card wa-bc-list" aria-label="Broadcasts">
            <div className="ws-controls wa-bc-controls">
              <WsSegmented label="Filter broadcasts" items={items} value={status} onChange={setStatus} />
              <div className="ws-controls-end">
                <label className="ws-searchbox">
                  <Search size={15} aria-hidden="true" />
                  <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search broadcasts" aria-label="Search broadcasts" />
                </label>
              </div>
            </div>
            <div className="wa-bc-row wa-list-head ws-table-head" aria-hidden="true">
              <span>Broadcast</span><span>Status</span><span>Results</span><span>When</span><span />
            </div>
            {state.loading && !state.rows.length && (
              <div style={{ padding: '8px 20px 16px', display: 'grid', gap: 14 }}>
                {[0, 1, 2].map((index) => <Skeleton key={index} height={44} radius={12} />)}
              </div>
            )}
            {!state.loading && !state.rows.length && (
              <div className="ws-empty" style={{ margin: '4px 16px 16px' }}>
                <strong>No broadcasts here</strong>
                {search ? `Nothing matches “${search}”.` : 'Try another filter.'}
              </div>
            )}
            <ul className="wa-list">{state.rows.map((row) => <BroadcastRow key={row.id} broadcast={row} />)}</ul>
          </section>
        </>
      )}
    </StagePage>
  );
}
